// Hand-typed mirror of the bot's auto-activate endpoint response.
// Source of truth lives in the bot repo at:
//   packages/bot/src/http/miniapp/types/auto-activate.types.ts
// Mirrored symbols (re-exported with Bot- prefix for cross-repo clarity):
//   - BotAutoActivateResponse  (mirrors source `AutoActivateResponse`)
//   - BotAutoActivateError     (mirrors source `AutoActivateError`)
// bot-auto-activate.spec.ts locks this list to the exact mirrored codes; the
// bot side is compile-checked by its exhaustive classifyOutcome switch. The
// two files are hand-maintained — edit them together when a code changes.

// Canonical 15-code union mirroring the bot source verbatim.
export type BotAutoActivateError =
  | 'TELEGRAM_AUTH_FAILED'
  | 'CARD_ACCESS_DENIED'
  | 'CARD_NOT_FOUND'
  | 'CARD_NOT_PHYSICAL'
  | 'CARD_NOT_DELIVERED'
  | 'CARD_ALREADY_ACTIVATED'
  | 'CARD_RATE_LIMITED'
  | 'WL_DAILY_CEILING_EXCEEDED'
  | 'USER_DAILY_LIMIT'
  | 'KEY_RATE_LIMITED'
  | 'ACTIVATION_CODE_RETRIEVE_FAILED'
  | 'ACTIVATION_TIMEOUT_RETRY_LATER'
  | 'PROVIDER_ERROR'
  | 'BOT_KEY_INVALID'
  | 'INTERNAL_ERROR';

export interface BotAutoActivateSuccess {
  readonly activated: true;
}

export interface BotAutoActivateFailure {
  readonly activated: false;
  readonly error: BotAutoActivateError;
  readonly retryAfter?: number;
}

export type BotAutoActivateResponse = BotAutoActivateSuccess | BotAutoActivateFailure;

// Membership predicate used by the FE parser to narrow an arbitrary string
// onto the locked error union without spreading the constants list across
// modules. Returning false means the bot regressed (or a third party is
// spoofing); the orchestrator collapses such payloads to PROVIDER_ERROR.
const ALL_ERRORS: ReadonlySet<BotAutoActivateError> = new Set<BotAutoActivateError>([
  'TELEGRAM_AUTH_FAILED',
  'CARD_ACCESS_DENIED',
  'CARD_NOT_FOUND',
  'CARD_NOT_PHYSICAL',
  'CARD_NOT_DELIVERED',
  'CARD_ALREADY_ACTIVATED',
  'CARD_RATE_LIMITED',
  'WL_DAILY_CEILING_EXCEEDED',
  'USER_DAILY_LIMIT',
  'KEY_RATE_LIMITED',
  'ACTIVATION_CODE_RETRIEVE_FAILED',
  'ACTIVATION_TIMEOUT_RETRY_LATER',
  'PROVIDER_ERROR',
  'BOT_KEY_INVALID',
  'INTERNAL_ERROR',
]);

export const isBotAutoActivateError = (value: unknown): value is BotAutoActivateError =>
  typeof value === 'string' && ALL_ERRORS.has(value as BotAutoActivateError);
